import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../utils/api";
import { useApp } from "../context/AppContext";
import { t } from "../utils/i18n";
import { Avatar, vibeRingKind } from "../components/Avatar";
import UiIcon from "../components/UiIcon";
import { Screen, PageHeader, RoundButton, Card, SectionTitle, Button, Chip, Segmented, InfoRow, fieldStyle, K } from "../components/UiKit";

type Tab = "infos" | "participants" | "gifts";
const hostOf = (url: string) => { try { return new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname.replace(/^www\./, ""); } catch { return url; } };

// Détail web d'un anniversaire — miroir de OLTODO/app/birthday-event/[id].tsx. La visibilité
// (403 si non-participant) est déjà garantie côté backend : ce composant ne fait qu'afficher ce
// que l'API a bien voulu renvoyer, aucune logique de confidentialité supplémentaire ici.
export default function BirthdayEventDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, lang } = useApp();
  const [event, setEvent] = useState<any>(null);
  const [participants, setParticipants] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState<Tab>("infos");
  const [busy, setBusy] = useState(false);

  const [showInviteMore, setShowInviteMore] = useState(false);
  const [friends, setFriends] = useState<any[]>([]);
  const [inviteSelected, setInviteSelected] = useState<string[]>([]);
  const [inviting, setInviting] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);

  const [gifts, setGifts] = useState<any[]>([]);
  const [giftTitle, setGiftTitle] = useState("");
  const [giftUrl, setGiftUrl] = useState("");
  const [addingGift, setAddingGift] = useState(false);
  const [giftFormOpen, setGiftFormOpen] = useState(false);
  const [claimingGiftId, setClaimingGiftId] = useState<string | null>(null);
  const [revealing, setRevealing] = useState(false);

  const loadParticipants = useCallback(() => {
    if (!id) return;
    api.get(`/birthday-events/${id}/participants`).then(d => setParticipants(d?.participants || [])).catch(() => {});
  }, [id]);

  const load = useCallback(() => {
    api.get(`/birthday-events/${id}`).then(d => {
      if (!d?.event) { setNotFound(true); return; }
      setEvent(d.event);
    }).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); loadParticipants(); }, [load, loadParticipants]);

  const isOrganizer = event?.my_role === "organizer";
  const isGuest = event?.my_role === "guest";
  const isBirthdayPerson = !!event && event.birthday_user_id === user?.id;

  const loadGifts = useCallback(() => {
    if (!id) return;
    api.get(`/birthday-events/${id}/gifts`).then(d => setGifts(d?.gifts || [])).catch(() => {});
  }, [id]);

  useEffect(() => { if (event && !isBirthdayPerson) loadGifts(); }, [event, isBirthdayPerson, loadGifts]);

  const addGift = () => {
    const title = giftTitle.trim();
    if (!title || addingGift) return;
    setAddingGift(true);
    api.post(`/birthday-events/${id}/gifts`, { title, url: giftUrl.trim() || undefined }).then(data => {
      if (data?.gift) { setGifts(prev => [...prev, data.gift]); setGiftTitle(""); setGiftUrl(""); setGiftFormOpen(false); }
      else alert(data?.error || t(lang, "error_connection"));
    }).catch(() => alert(t(lang, "error_connection"))).finally(() => setAddingGift(false));
  };

  const toggleClaimGift = (gift: any) => {
    if (claimingGiftId) return;
    const claiming = gift.claimed_by_user_id !== user?.id;
    setClaimingGiftId(gift.id);
    api.put(`/birthday-events/${id}/gifts/${gift.id}/claim`, { claim: claiming }).then(data => {
      if (data?.gift) setGifts(prev => prev.map(g => g.id === gift.id ? data.gift : g));
      else alert(data?.error === "Déjà réservé par quelqu'un d'autre" ? t(lang, "bday_gift_error_duplicate") : t(lang, "error_connection"));
    }).catch(() => alert(t(lang, "error_connection"))).finally(() => setClaimingGiftId(null));
  };

  const deleteGift = (giftId: string) => {
    api.delete(`/birthday-events/${id}/gifts/${giftId}`).then(data => {
      if (data && !data.error) setGifts(prev => prev.filter(g => g.id !== giftId));
      else alert(t(lang, "error_connection"));
    }).catch(() => alert(t(lang, "error_connection")));
  };

  const revealSurprise = () => {
    if (!window.confirm(`${t(lang, "bday_reveal_confirm_title")} ${event.birthday_user_name}${t(lang, "bday_reveal_confirm_body_p2")}`)) return;
    if (revealing) return;
    setRevealing(true);
    api.post(`/birthday-events/${id}/reveal`, {}).then(data => {
      if (data?.event) setEvent((e: any) => ({ ...e, revealed_at: data.event.revealed_at }));
      else alert(data?.error || t(lang, "error_connection"));
    }).catch(() => alert(t(lang, "error_connection"))).finally(() => setRevealing(false));
  };

  const respondRsvp = (status: "accepted" | "declined") => {
    if (busy) return;
    setBusy(true);
    api.put(`/birthday-events/${id}/rsvp`, { status }).then(data => {
      if (data && !data.error) setEvent((e: any) => ({ ...e, my_rsvp: status }));
      else alert(data?.error || t(lang, "error_connection"));
    }).catch(() => alert(t(lang, "error_connection"))).finally(() => setBusy(false));
  };

  const cancelEvent = () => {
    if (!window.confirm(`${t(lang, "bday_cancel_confirm_title")} ${t(lang, "bday_cancel_confirm_body")}`)) return;
    api.delete(`/birthday-events/${id}`).then(data => {
      if (data && !data.error) navigate("/birthday-events");
      else alert(data?.error || t(lang, "error_connection"));
    }).catch(() => alert(t(lang, "error_connection")));
  };

  const openInviteMore = () => {
    setShowInviteMore(true);
    if (user?.id && friends.length === 0) {
      api.get(`/users/${user.id}/friends`).then(d => setFriends(d?.friends || [])).catch(() => {});
    }
  };

  const toggleInvite = (fid: string) => setInviteSelected(prev => prev.includes(fid) ? prev.filter(x => x !== fid) : [...prev, fid]);

  const sendMoreInvites = () => {
    if (inviting || inviteSelected.length === 0) return;
    setInviting(true);
    api.post(`/birthday-events/${id}/invite`, { guest_ids: inviteSelected }).then(data => {
      if (data && !data.error) {
        setInviteSelected([]);
        setShowInviteMore(false);
        setInviteSent(true);
        loadParticipants();
        setEvent((e: any) => ({ ...e, participants_count: (e?.participants_count || 0) + inviteSelected.length }));
      } else {
        alert(data?.error || t(lang, "error_connection"));
      }
    }).catch(() => alert(t(lang, "error_connection"))).finally(() => setInviting(false));
  };

  if (loading) return <Screen><div style={{ textAlign: "center", padding: 60, color: K.faint }}>{t(lang, "msg_loading")}</div></Screen>;
  if (notFound || !event) return <Screen><div style={{ textAlign: "center", padding: 60, color: K.faint }}>{t(lang, "bday_not_found")}</div></Screen>;

  const dateLabel = event.event_date ? new Date(event.event_date).toLocaleDateString(lang, { weekday: "long", day: "numeric", month: "long" }) : "";
  const schedule = [dateLabel, event.event_time].filter(Boolean).join(" · ");
  const inviteExcludeIds = new Set([...participants.map((p: any) => p.id), ...(event.is_surprise ? [event.birthday_user_id] : [])]);
  const invitePool = friends.filter(f => !inviteExcludeIds.has(f.id));
  const tabs: Tab[] = isBirthdayPerson ? ["infos", "participants"] : ["infos", "participants", "gifts"];
  const claimedCount = gifts.filter(g => !!g.claimed_by_user_id).length;
  const statusOf = (p: any) => p.role === "organizer" ? { label: t(lang, "bday_organizer_label"), icon: "user" as const, ink: true }
    : p.rsvp_status === "accepted" ? { label: t(lang, "bday_status_coming"), icon: "check" as const, ink: false }
    : p.rsvp_status === "declined" ? { label: t(lang, "bday_status_not_coming"), icon: "close" as const, ink: false }
    : { label: t(lang, "bday_status_pending"), icon: "clock" as const, ink: false };

  return (
    <Screen>
      <PageHeader
        title={event.title}
        onBack={() => navigate(-1)}
        right={event.conversation_id ? <RoundButton icon="chat" label={t(lang, "community_chat")} onClick={() => navigate(`/messages/${event.conversation_id}`)} /> : undefined}
      />

      {!!event.is_surprise && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, backgroundColor: K.surface, borderRadius: 14, padding: "12px 14px", marginBottom: 14 }}>
          <div style={{ width: 34, height: 34, borderRadius: 17, backgroundColor: K.ink, color: K.white, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <UiIcon name={event.revealed_at ? "sparkle" : "eyeOff"} size={17} />
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.4, color: K.ink }}>
            {!event.revealed_at
              ? <>{t(lang, "bday_surprise_banner_p1")}{event.birthday_user_name}{t(lang, "bday_surprise_banner_p2")}</>
              : <>{t(lang, "bday_revealed_banner_p1")}{event.birthday_user_name}{t(lang, "bday_revealed_banner_p2")}</>}
          </div>
        </div>
      )}

      {isGuest && event.my_rsvp !== "accepted" && event.my_rsvp !== "declined" && (
        <Card>
          <div style={{ fontSize: 14.5, fontWeight: 700, marginBottom: 12 }}>{t(lang, "bday_rsvp_question")}</div>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 10 }}>
            <Button variant="secondary" small disabled={busy} icon="close" onClick={() => respondRsvp("declined")}>{t(lang, "bday_rsvp_no")}</Button>
            <Button small disabled={busy} icon="check" onClick={() => respondRsvp("accepted")}>{t(lang, "bday_rsvp_yes")}</Button>
          </div>
        </Card>
      )}
      {isGuest && event.my_rsvp === "accepted" && (
        <Card style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <UiIcon name="check" size={18} />
          <span style={{ fontSize: 13.5, fontWeight: 600, flex: 1 }}>{t(lang, "bday_rsvp_accepted")}</span>
          <button onClick={() => respondRsvp("declined")} style={{ fontSize: 12.5, color: K.muted, textDecoration: "underline" }}>{t(lang, "bday_rsvp_change_mind")}</button>
        </Card>
      )}

      <Segmented<Tab>
        tabs={tabs.map(tb => ({ key: tb, icon: tb === "infos" ? "info" : tb === "participants" ? "users" : "gift", label: t(lang, tb === "infos" ? "bday_tab_infos" : tb === "participants" ? "bday_tab_participants" : "bday_tab_gifts") }))}
        value={tab}
        onChange={setTab}
      />

      {tab === "infos" ? (
        <>
          {!!event.description && (
            <Card>
              <SectionTitle>{t(lang, "bday_section_program")}</SectionTitle>
              <div style={{ fontSize: 14, lineHeight: 1.55, whiteSpace: "pre-wrap", overflowWrap: "anywhere", color: K.ink }}>{event.description}</div>
            </Card>
          )}
          <Card style={{ paddingBottom: 6 }}>
            <SectionTitle>{t(lang, "bday_section_info")}</SectionTitle>
            <InfoRow icon="cake" label={t(lang, "bday_for_prefix")} value={event.birthday_user_name} />
            {!!schedule && <InfoRow icon="calendar" label={t(lang, "bday_field_when")} value={schedule} />}
            {!!event.location_name && <InfoRow icon="pin" label={t(lang, "bday_field_where")} value={event.location_name} />}
            <InfoRow icon="users" label={t(lang, "bday_tab_participants")} value={String(event.participants_count || 0)} last />
          </Card>

          {isOrganizer && !showInviteMore && (
            <div style={{ marginBottom: 12 }}><Button variant="secondary" icon="userPlus" onClick={openInviteMore}>{t(lang, "bday_invite_more_btn")}</Button></div>
          )}
          {isOrganizer && showInviteMore && (
            <Card>
              {invitePool.length === 0 ? (
                <div style={{ fontSize: 13, color: K.faint, marginBottom: 6 }}>{t(lang, "bday_no_friends")}</div>
              ) : (
                <div style={{ maxHeight: 280, overflowY: "auto", overflowX: "hidden", marginBottom: 12 }}>
                  {invitePool.map(f => {
                    const active = inviteSelected.includes(f.id);
                    return (
                      <div key={f.id} onClick={() => toggleInvite(f.id)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0", cursor: "pointer", minWidth: 0 }}>
                        <Avatar id={f.id} name={f.name} uri={f.avatar_url} size={42} />
                        <span style={{ flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                        <span style={{ width: 24, height: 24, borderRadius: 8, border: `2px solid ${active ? K.ink : "#CFCFCF"}`, backgroundColor: active ? K.ink : "transparent", display: "flex", alignItems: "center", justifyContent: "center", color: K.white, flexShrink: 0 }}>
                          {active && <UiIcon name="check" size={14} strokeWidth={2.6} />}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              <Button onClick={sendMoreInvites} disabled={inviting || inviteSelected.length === 0}>{t(lang, "bday_invite_friends_label")}</Button>
            </Card>
          )}
          {isOrganizer && inviteSent && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontSize: 12.5, color: K.muted, marginBottom: 12 }}><UiIcon name="check" size={14} />{t(lang, "bday_invite_sent")}</div>
          )}

          {isOrganizer && event.is_surprise && !event.revealed_at && (
            <div style={{ marginBottom: 12 }}><Button variant="secondary" icon="sparkle" disabled={revealing} onClick={revealSurprise}>{revealing ? t(lang, "bday_submitting") : t(lang, "bday_reveal_btn")}</Button></div>
          )}
          {isOrganizer && (
            <Button variant="danger" onClick={cancelEvent}>{t(lang, "bday_cancel_event_btn")}</Button>
          )}
        </>
      ) : tab === "participants" ? (
        <div>
          {participants.map((p: any) => {
            const st = statusOf(p);
            return (
              <div key={p.id} onClick={() => navigate(`/profile/${p.id}`)} style={{ display: "flex", alignItems: "center", gap: 12, backgroundColor: K.white, border: `1px solid ${K.line}`, borderRadius: 16, padding: 12, marginBottom: 10, cursor: "pointer", minWidth: 0 }}>
                <Avatar id={p.id} name={p.name} uri={p.avatar_url} size={44} ringKind={vibeRingKind(p.has_real, p.has_active)} />
                <div style={{ flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                <Chip ink={st.ink} icon={st.icon}>{st.label}</Chip>
              </div>
            );
          })}
        </div>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: K.muted, marginBottom: 12, lineHeight: 1.4 }}>
            <UiIcon name="lock" size={14} />{t(lang, "bday_gifts_secret_note")}
          </div>

          {gifts.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                <span>{t(lang, "bday_gifts_summary").replace("{n}", String(gifts.length)).replace("{m}", String(claimedCount))}</span>
              </div>
              <div style={{ height: 6, borderRadius: 3, backgroundColor: K.surface, overflow: "hidden" }}>
                <div style={{ width: `${(claimedCount / gifts.length) * 100}%`, height: "100%", backgroundColor: K.ink, transition: "width .3s" }} />
              </div>
            </div>
          )}

          {!giftFormOpen ? (
            <div style={{ marginBottom: 14 }}><Button variant="secondary" icon="plus" onClick={() => setGiftFormOpen(true)}>{t(lang, "bday_gift_propose")}</Button></div>
          ) : (
            <Card>
              <input autoFocus value={giftTitle} onChange={e => setGiftTitle(e.target.value.slice(0, 120))} placeholder={t(lang, "bday_gift_add_placeholder")} style={{ ...fieldStyle, marginBottom: 10 }} />
              <input value={giftUrl} onChange={e => setGiftUrl(e.target.value)} placeholder={t(lang, "bday_gift_url_placeholder")} autoCapitalize="none" style={{ ...fieldStyle, marginBottom: 12 }} />
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1.4fr)", gap: 10 }}>
                <Button variant="soft" small onClick={() => { setGiftFormOpen(false); setGiftTitle(""); setGiftUrl(""); }}>{t(lang, "bday_cancel_no")}</Button>
                <Button small onClick={addGift} disabled={!giftTitle.trim() || addingGift}>{t(lang, "bday_gift_add_btn")}</Button>
              </div>
            </Card>
          )}

          {gifts.length === 0 ? (
            <div style={{ textAlign: "center", padding: "28px 12px" }}>
              <div style={{ width: 64, height: 64, borderRadius: 32, backgroundColor: K.surface, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}><UiIcon name="gift" size={28} /></div>
              <div style={{ fontSize: 13.5, color: K.muted }}>{t(lang, "bday_gifts_empty")}</div>
            </div>
          ) : (
            gifts.map((g: any) => {
              const mine = g.claimed_by_user_id === user?.id;
              const takenByOther = !!g.claimed_by_user_id && !mine;
              const canDelete = g.added_by_user_id === user?.id || isOrganizer;
              const busyThis = claimingGiftId === g.id;
              return (
                <div key={g.id} style={{ backgroundColor: K.white, border: `1px solid ${mine ? K.ink : K.line}`, borderRadius: 16, padding: 14, marginBottom: 10, minWidth: 0, opacity: takenByOther ? 0.75 : 1 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 12, minWidth: 0 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: K.surface, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <UiIcon name="gift" size={22} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, overflowWrap: "anywhere", color: K.ink }}>{g.title}</div>
                      <div style={{ fontSize: 12.5, color: K.faint, marginTop: 2 }}>{t(lang, "bday_gift_added_by_prefix")} {g.added_by_name}</div>
                      {g.url && (
                        <a href={/^https?:\/\//i.test(g.url) ? g.url : `https://${g.url}`} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 8, height: 26, padding: "0 10px", borderRadius: 13, backgroundColor: K.surface, fontSize: 12, fontWeight: 600, color: K.ink, maxWidth: "100%" }}>
                          <UiIcon name="link" size={13} /><span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{hostOf(g.url)}</span>
                        </a>
                      )}
                    </div>
                    {canDelete && (
                      <button onClick={() => deleteGift(g.id)} aria-label={t(lang, "msg_delete_action")} style={{ width: 32, height: 32, borderRadius: 16, color: K.faint, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <UiIcon name="trash" size={17} />
                      </button>
                    )}
                  </div>
                  <div style={{ marginTop: 12 }}>
                    {takenByOther ? (
                      <Button variant="soft" small disabled icon="check">{`${t(lang, "bday_gift_claimed_by_prefix")} ${g.claimed_by_name}`}</Button>
                    ) : mine ? (
                      <Button variant="secondary" small disabled={busyThis} icon="close" onClick={() => toggleClaimGift(g)}>{t(lang, "bday_gift_claimed_by_me")}</Button>
                    ) : (
                      <Button small disabled={busyThis} onClick={() => toggleClaimGift(g)}>{busyThis ? "…" : t(lang, "bday_gift_claim_btn")}</Button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </>
      )}
    </Screen>
  );
}
